// The `json:studio` fence: how the AI Helper asks the Studio to do
// something. Parsing and labelling live here, host-free, so the chat can
// render a fence even where no Studio is listening; which actions exist
// and which group they fall in is fixed here too — the fence never says
// "auto" or "click", the registry does.

export const STUDIO_FENCE_ARTIFACT = "studio";

export type StageRole = "source" | "transform" | "sink" | "schema" | "tests";

export type StudioTab = "source" | "transform" | "sink" | "tests";
export type StudioViewName = "stages" | "full";

export type StudioAction =
  | { action: "explorer.expand"; ids: string[] }
  | { action: "explorer.collapse"; ids: string[] }
  | { action: "explorer.filter"; text: string }
  | { action: "stage"; id: string; role?: StageRole }
  | { action: "tab.set"; tab: StudioTab }
  | { action: "view.set"; view: StudioViewName }
  | { action: "select"; id: string }
  | { action: "output.set"; collapsed: boolean }
  | { action: "run.pipeline" }
  | { action: "run.script"; id: string }
  | { action: "run.tests" }
  | { action: "unstage"; id: string }
  | { action: "reorder"; id: string; before?: string; after?: string }
  | { action: "schema.attach"; schema: string; stage: string }
  | { action: "schema.detach"; schema: string; stage: string }
  | { action: "input.set"; sample?: InputSample; from?: string; text?: string }
  | { action: "script.template"; template: string }
  | { action: "pipeline.template" };

export type InputSample = "document" | "batch";

export type StudioActionGroup = "auto" | "click";

const ROLES: ReadonlySet<string> = new Set([
  "source",
  "transform",
  "sink",
  "schema",
  "tests",
]);

const ROLE_LABELS: Record<StageRole, string> = {
  source: "Source",
  transform: "Transform",
  sink: "Sink",
  schema: "Schema",
  tests: "Tests",
};

const TABS: ReadonlySet<string> = new Set([
  "source",
  "transform",
  "sink",
  "tests",
]);
const VIEWS: ReadonlySet<string> = new Set(["stages", "full"]);

// Click actions change the pipeline or spend a run; everything else only
// changes what the user sees and applies on its own.
const CLICK_ACTIONS: ReadonlySet<StudioAction["action"]> = new Set([
  "stage",
  "run.pipeline",
  "run.script",
  "run.tests",
  "unstage",
  "reorder",
  "schema.attach",
  "schema.detach",
  "input.set",
  "script.template",
  "pipeline.template",
]);

export function groupOf(action: StudioAction): StudioActionGroup {
  return CLICK_ACTIONS.has(action.action) ? "click" : "auto";
}

/** The button text of a click action's row. */
export function actionButton(action: StudioAction): string {
  if (action.action === "stage") return "Stage";
  return action.action.startsWith("run.") ? "Run" : "Apply";
}

function isStringList(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

type Read = { action: StudioAction } | { error: string };
type Reader = (r: Record<string, unknown>) => Read;

function isRole(v: unknown): v is StageRole {
  return typeof v === "string" && ROLES.has(v);
}

function readStage(r: Record<string, unknown>): Read {
  if (typeof r.id !== "string" || r.id === "") {
    return { error: "stage needs an id" };
  }
  if (r.role !== undefined && !isRole(r.role)) {
    return { error: `unknown role ${JSON.stringify(r.role)}` };
  }
  const role = r.role as StageRole | undefined;
  return { action: { action: "stage", id: r.id, ...(role ? { role } : {}) } };
}

function readIds(name: "explorer.expand" | "explorer.collapse"): Reader {
  return (r) =>
    isStringList(r.ids)
      ? { action: { action: name, ids: r.ids } }
      : { error: `${name} needs ids` };
}

function readFilter(r: Record<string, unknown>): Read {
  const text = typeof r.text === "string" ? r.text : "";
  return { action: { action: "explorer.filter", text } };
}

// A reader for an action that carries one required id.
function readId(name: "select" | "run.script" | "unstage"): Reader {
  return (r) =>
    typeof r.id === "string" && r.id !== ""
      ? { action: { action: name, id: r.id } }
      : { error: `${name} needs an id` };
}

// A reader for an action whose one field must be one of a fixed set.
function readChoice<K extends string>(
  name: "tab.set" | "view.set",
  field: K,
  allowed: ReadonlySet<string>,
): Reader {
  return (r) => {
    const value = r[field];
    if (typeof value !== "string" || !allowed.has(value)) {
      return { error: `unknown ${field} ${JSON.stringify(value)}` };
    }
    return { action: { action: name, [field]: value } as StudioAction };
  };
}

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v !== "" ? v : undefined;
}

// reorder: the moved id plus exactly one anchor.
function readReorder(r: Record<string, unknown>): Read {
  const id = optionalString(r.id);
  const before = optionalString(r.before);
  const after = optionalString(r.after);
  if (!id) return { error: "reorder needs an id" };
  if (!!before === !!after) return { error: "reorder needs before or after" };
  return {
    action: { action: "reorder", id, ...(before ? { before } : { after }) },
  };
}

function readSchemaTarget(name: "schema.attach" | "schema.detach"): Reader {
  return (r) => {
    const schema = optionalString(r.schema);
    const stage = optionalString(r.stage);
    if (!schema || !stage) return { error: `${name} needs schema and stage` };
    return { action: { action: name, schema, stage } };
  };
}

const SAMPLES: ReadonlySet<string> = new Set(["document", "batch"]);

// input.set: exactly one of sample / from / text.
function readInput(r: Record<string, unknown>): Read {
  const sample = optionalString(r.sample);
  const from = optionalString(r.from);
  const text = typeof r.text === "string" ? r.text : undefined;
  const given = [sample, from, text].filter((v) => v !== undefined).length;
  if (given !== 1)
    return { error: "input.set needs one of sample, from or text" };
  if (sample !== undefined) {
    return SAMPLES.has(sample)
      ? { action: { action: "input.set", sample: sample as InputSample } }
      : { error: `unknown sample ${JSON.stringify(sample)}` };
  }
  return from !== undefined
    ? { action: { action: "input.set", from } }
    : { action: { action: "input.set", text: text ?? "" } };
}

// script.template: any id — the Studio checks it against its catalog.
function readTemplate(r: Record<string, unknown>): Read {
  const template = optionalString(r.template);
  return template
    ? { action: { action: "script.template", template } }
    : { error: "script.template needs a template" };
}

function readOutput(r: Record<string, unknown>): Read {
  return typeof r.collapsed === "boolean"
    ? { action: { action: "output.set", collapsed: r.collapsed } }
    : { error: "output.set needs collapsed: true or false" };
}

// One reader per action name — the registry of what the fence may hold.
const READERS = new Map<string, Reader>([
  ["explorer.expand", readIds("explorer.expand")],
  ["explorer.collapse", readIds("explorer.collapse")],
  ["explorer.filter", readFilter],
  ["stage", readStage],
  ["tab.set", readChoice("tab.set", "tab", TABS)],
  ["view.set", readChoice("view.set", "view", VIEWS)],
  ["select", readId("select")],
  ["output.set", readOutput],
  ["run.pipeline", () => ({ action: { action: "run.pipeline" } })],
  ["run.script", readId("run.script")],
  ["run.tests", () => ({ action: { action: "run.tests" } })],
  ["unstage", readId("unstage")],
  ["reorder", readReorder],
  ["schema.attach", readSchemaTarget("schema.attach")],
  ["schema.detach", readSchemaTarget("schema.detach")],
  ["input.set", readInput],
  ["script.template", readTemplate],
  ["pipeline.template", () => ({ action: { action: "pipeline.template" } })],
]);

function asRecord(raw: unknown): Record<string, unknown> {
  return typeof raw === "object" && raw !== null
    ? (raw as Record<string, unknown>)
    : {};
}

// One raw entry → an action, or the reason it isn't one.
function readAction(raw: unknown): Read {
  const r = asRecord(raw);
  const name = typeof r.action === "string" ? r.action : "";
  const reader = READERS.get(name);
  return reader
    ? reader(r)
    : { error: `unknown action ${JSON.stringify(name)}` };
}

export function parseStudioActions(code: string): {
  actions: StudioAction[];
  errors: string[];
} {
  let parsed: unknown;
  try {
    parsed = JSON.parse(code);
  } catch {
    return { actions: [], errors: ["couldn't read this action"] };
  }
  const list = Array.isArray(parsed) ? parsed : [parsed];
  const actions: StudioAction[] = [];
  const errors: string[] = [];
  for (const raw of list) {
    const read = readAction(raw);
    if ("action" in read) actions.push(read.action);
    else errors.push(read.error);
  }
  return { actions, errors };
}

// How an id reads in the chat — never an internal form. Resources show
// their name (the service prefix never holds a colon, names may); catalog
// ids (schemas, suites) show a short prefix of their uuid; the Studio's
// editor pseudo-rows and schema-instance ids ("<schema>|<block>") are
// named, not echoed. Known ids are relabelled by the host before this
// runs; this is the floor for whatever the model sends.
export function displayName(id: string): string {
  const bare = id.split("|")[0] ?? id;
  if (bare.startsWith("schema:editor")) return "the schema editor";
  if (bare.endsWith(":editor")) return "the editor";
  if (bare.startsWith("resource:")) return afterService(bare);
  if (bare.startsWith("service:")) return bare.slice("service:".length);
  const catalog = /^(schema|suite):(.{8})/.exec(bare);
  if (catalog && bare.length > catalog[0].length) {
    return `${catalog[1]} ${catalog[2]}…`;
  }
  return bare;
}

function afterService(resourceId: string): string {
  const rest = resourceId.slice("resource:".length);
  const colon = rest.indexOf(":");
  return colon === -1 ? rest : rest.slice(colon + 1);
}

function impliedRole(action: Extract<StudioAction, { action: "stage" }>) {
  if (action.role) return action.role;
  if (action.id.startsWith("schema:")) return "schema";
  if (action.id.startsWith("suite:")) return "tests";
  return undefined;
}

const TAB_LABELS: Record<StudioTab, string> = {
  source: "Source",
  transform: "Transform",
  sink: "Sink",
  tests: "Tests",
};

type ActionOf<K extends StudioAction["action"]> = Extract<
  StudioAction,
  { action: K }
>;
type Labelers = {
  [K in StudioAction["action"]]: (action: ActionOf<K>) => string;
};

function stageLabel(action: ActionOf<"stage">): string {
  const role = impliedRole(action);
  return `${displayName(action.id)} → ${role ? ROLE_LABELS[role] : "?"}`;
}

function inputLabel(a: ActionOf<"input.set">): string {
  if (a.sample === "document") return "Input ← sample document";
  if (a.sample === "batch") return "Input ← batch documents";
  return a.from ? `Input ← ${displayName(a.from)}` : "Input ← text";
}

// One labeler per action — the row text the chat shows.
const LABELERS: Labelers = {
  "explorer.expand": (a) => `Explorer — expand ${a.ids.join(", ")}`,
  "explorer.collapse": (a) => `Explorer — collapse ${a.ids.join(", ")}`,
  "explorer.filter": (a) =>
    a.text === ""
      ? "Explorer — clear filter"
      : `Explorer — filter ${JSON.stringify(a.text)}`,
  stage: stageLabel,
  "tab.set": (a) => `Studio — ${TAB_LABELS[a.tab]} tab`,
  "view.set": (a) =>
    a.view === "full" ? "Studio — Full pipeline view" : "Studio — Stages view",
  select: (a) => `Select ${displayName(a.id)}`,
  "output.set": (a) => `Output panel — ${a.collapsed ? "hide" : "show"}`,
  "run.pipeline": () => "Run pipeline",
  "run.script": (a) => `Run ${displayName(a.id)}`,
  "run.tests": () => "Run all tests",
  unstage: (a) => `Remove ${displayName(a.id)}`,
  reorder: (a) =>
    a.before
      ? `Move ${displayName(a.id)} before ${displayName(a.before)}`
      : `Move ${displayName(a.id)} after ${displayName(a.after ?? "")}`,
  "schema.attach": (a) =>
    `Attach ${displayName(a.schema)} to ${displayName(a.stage)}`,
  "schema.detach": (a) =>
    `Detach ${displayName(a.schema)} from ${displayName(a.stage)}`,
  "input.set": inputLabel,
  "script.template": (a) => `Script ← ${a.template} template`,
  "pipeline.template": () => "Pipeline ← template",
};

export function actionLabel(action: StudioAction): string {
  const label = LABELERS[action.action] as (a: StudioAction) => string;
  return label(action);
}

const FENCE = /```json:studio[^\n]*\n([\s\S]*?)```/g;

export function extractStudioFences(markdown: string): string[] {
  return [...markdown.matchAll(FENCE)].map((m) => m[1].replace(/\n$/, ""));
}
